/**
 * Placeholder mech (P1 surface — replaced in P3 by `ArchetypeMech`).
 *
 * Renders one mech as a colored box at its hex position with a forward-
 * pointing arrow so facing reads at a glance. Tweens position + yaw
 * over `tweenMs` so movement between turns reads as motion, not as a
 * teleport.
 *
 * The placeholder is what P2 lights with PBR + post-processing. If the
 * scene doesn't look premium with cubes, no voxel content saves it
 * later — that's the PRD §6.3 / P2 commitment.
 */

import { useEffect, useMemo, useRef } from "react";
import type { Mesh } from "three";
import { useFrame } from "@react-three/fiber";
import type { MechState } from "@pilot-protocol/replay-parser";
import { facingToYaw, hexToWorld } from "./hexMath";

interface PlaceholderMechProps {
  mech: MechState;
  color: string;
  /** Wall-clock ms over which to tween between updates (default 400). */
  tweenMs?: number;
}

export function PlaceholderMech({
  mech,
  color,
  tweenMs = 400,
}: PlaceholderMechProps) {
  const groupRef = useRef<Mesh>(null);
  const target = useMemo(() => {
    const w = hexToWorld(mech.position, 0);
    return { x: w.x, y: 0.5, z: w.z, yaw: facingToYaw(mech.facing) };
  }, [mech.position.q, mech.position.r, mech.facing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Snap on first mount so the mech starts where the state says it is
  // instead of tweening in from the origin.
  useEffect(() => {
    const m = groupRef.current;
    if (!m) return;
    m.position.set(target.x, target.y, target.z);
    m.rotation.y = target.yaw;
    // Only fire on mount; subsequent updates handled by useFrame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((_, dt) => {
    const m = groupRef.current;
    if (!m) return;
    const alpha = Math.min(1, (dt * 1000) / tweenMs);
    m.position.x += (target.x - m.position.x) * alpha;
    m.position.y += (target.y - m.position.y) * alpha;
    m.position.z += (target.z - m.position.z) * alpha;
    m.rotation.y += shortestAngle(m.rotation.y, target.yaw) * alpha;
  });

  const destroyed = mech.front_ct.internal <= 0;

  return (
    <mesh
      ref={groupRef}
      castShadow
      receiveShadow
      visible={!destroyed}
    >
      <boxGeometry args={[1, 1, 1.2]} />
      <meshStandardMaterial
        color={color}
        roughness={0.45}
        metalness={0.5}
        emissive={color}
        emissiveIntensity={mech.heat > 0 ? Math.min(0.6, mech.heat / 100) : 0}
      />
      {/* Forward arrow — small triangle in front of the box so facing is readable. */}
      <mesh position={[0, 0, 0.7]} rotation={[0, 0, Math.PI / 2]}>
        <coneGeometry args={[0.18, 0.4, 4]} />
        <meshStandardMaterial color="#f8fafc" emissive="#f8fafc" emissiveIntensity={0.2} />
      </mesh>
    </mesh>
  );
}

/** Returns the signed minimal angle from `from` to `to` in radians, in (-π, π]. */
function shortestAngle(from: number, to: number): number {
  const two = Math.PI * 2;
  let d = ((to - from) % two + two) % two;
  if (d > Math.PI) d -= two;
  return d;
}
